import { matchPath } from 'react-router-dom';
import { BASE_PERMISSION, type PermissionAction } from '@/lib/constants/modules';
import { isGroup, NAV_ITEMS } from '@/components/layout/nav-config';

/**
 * Page-level ACL rules: which ACL permission a URL needs.
 *
 *  - Sub-superadmin: needs `action` on `path` in their own ACL.
 *  - Reseller / user under a sub-superadmin: only rules with `inherited` apply
 *    (same modules the sidebar hides via `inheritedModule`) — the nearest
 *    sub-superadmin's ACL must grant view on that module.
 *  - Everyone else (superadmin tree, no sub-superadmin above): never blocked.
 */
export interface AclRouteRule {
  pattern: string;
  path: string;
  action: PermissionAction;
  inherited?: string;
}

const { ADD, EDIT, VIEW } = BASE_PERMISSION;

/** Hidden (create / update / bulk) pages that have no sidebar entry. Checked first. */
const MANUAL_RULES: AclRouteRule[] = [
  // Manage Users
  { pattern: '/create-user', path: 'Manage Users.Create User', action: ADD },
  { pattern: '/sub-super-admin/create-user', path: 'Manage Users.Create User', action: ADD },
  { pattern: '/update-user/:userId', path: 'Manage Users.Directory', action: EDIT },
  { pattern: '/update-sub-user/:userId', path: 'Manage Users.Directory', action: EDIT },
  { pattern: '/sub-super-admin/update-user/:userId', path: 'Manage Users.Directory', action: EDIT },
  { pattern: '/user-settings/edit/:subId/:userId', path: 'Manage Users.Tier Pricing', action: EDIT },
  { pattern: '/update-user_setting/:ids', path: 'Manage Users.Tier Pricing', action: EDIT },
  // Manage Groups
  { pattern: '/create-group', path: 'Manage Groups.Create Group', action: ADD },
  { pattern: '/update-group/:groupId', path: 'Manage Groups.Group List', action: EDIT },
  { pattern: '/bulk-addition-group', path: 'Manage Groups.Bulk Group Add', action: ADD },
  { pattern: '/bulk-update-group', path: 'Manage Groups.Bulk Group Add', action: ADD },
  // Manage Locations — its child rows are not exposed in the role matrix, so the
  // whole module is gated on its (derived) view flag.
  { pattern: '/create-location', path: 'Manage Locations', action: VIEW },
  { pattern: '/update-location/:locationId', path: 'Manage Locations', action: VIEW },
  { pattern: '/bulk-addition-location', path: 'Manage Locations', action: VIEW },
  { pattern: '/bulk-location-delete', path: 'Manage Locations', action: VIEW },
  // Manage Dialers
  { pattern: '/create-dialer', path: 'Manage Dialers.Create Dialer', action: ADD },
  { pattern: '/update-dialer/:dialerId', path: 'Manage Dialers.Dialer List', action: EDIT },
  // Manage Trunks
  { pattern: '/create-trunk', path: 'Manage Trunks.Create Trunk', action: ADD },
  { pattern: '/update-trunk/:trunkId', path: 'Manage Trunks.Update Trunk', action: EDIT },
  // Manage CLI / API CLI
  { pattern: '/add-cli', path: 'Manage CLI.Upload CLI', action: ADD },
  { pattern: '/upload-cli', path: 'Manage CLI.Upload CLI', action: ADD },
  { pattern: '/upload-dedicated-cli', path: 'Manage CLI.Upload CLI', action: ADD },
  { pattern: '/update-cli/:cliId', path: 'Manage CLI.CLI List', action: EDIT },
  { pattern: '/upload-api-cli', path: 'Manage API CLI.Upload Api CLI', action: ADD },
  // Inbound
  { pattern: '/add-inbound-campaign', path: 'Inbound.Create Campaign', action: ADD, inherited: 'Inbound' },
  { pattern: '/clone-inbound-campaign/:campaignId', path: 'Inbound.Clone Campaign', action: ADD, inherited: 'Inbound' },
  // API Docs
  { pattern: '/api-docs', path: 'API Docs', action: VIEW, inherited: 'API Docs' },
  { pattern: '/api-docs/*', path: 'API Docs', action: VIEW, inherited: 'API Docs' },
  { pattern: '/api-docs-reference', path: 'API Docs', action: VIEW, inherited: 'API Docs' },
  { pattern: '/superadmin-api-docs/sheet-content', path: 'API Docs.Sheet Content', action: VIEW, inherited: 'API Docs' },
  { pattern: '/superadmin-api-docs/sheet-content/*', path: 'API Docs.Sheet Content', action: VIEW, inherited: 'API Docs' },
  { pattern: '/superadmin-api-docs/upload-document', path: 'API Docs.Upload Document', action: VIEW, inherited: 'API Docs' },
  { pattern: '/superadmin-api-docs/upload-document/*', path: 'API Docs.Upload Document', action: VIEW, inherited: 'API Docs' },
  // Number Action
  { pattern: '/add-voice-blacklist', path: 'Number Action.View Blacklist', action: VIEW },
  { pattern: '/bulk-voice-blacklist', path: 'Number Action.View Blacklist', action: VIEW },
  { pattern: '/add-blacklist-dnd', path: 'Number Action.Manage Blacklist-DND', action: VIEW },
  { pattern: '/add-whitelist', path: 'Number Action.Add Whitelist', action: ADD },
];

/** Every sidebar leaf that carries a `permissionPath` becomes a view rule automatically. */
function buildNavRules(): AclRouteRule[] {
  const out: AclRouteRule[] = [];
  for (const item of NAV_ITEMS) {
    if (isGroup(item)) {
      for (const child of item.children) {
        if (!child.permissionPath) continue;
        const inherited = child.inheritedModule ?? item.inheritedModule;
        out.push({
          pattern: child.to,
          path: child.permissionPath,
          action: VIEW,
          ...(inherited ? { inherited } : {}),
        });
      }
    } else if (item.permissionPath) {
      out.push({
        pattern: item.to,
        path: item.permissionPath,
        action: VIEW,
        ...(item.inheritedModule ? { inherited: item.inheritedModule } : {}),
      });
    }
  }
  return out;
}

const ACL_ROUTE_RULES: AclRouteRule[] = [...MANUAL_RULES, ...buildNavRules()];

export function findAclRule(pathname: string): AclRouteRule | null {
  const norm = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return ACL_ROUTE_RULES.find((r) => matchPath({ path: r.pattern, end: true }, norm)) ?? null;
}
